// React
import React, { useRef, useState, useEffect, Fragment } from 'react'
import PropTypes from 'prop-types'

// Libraries
import { useFormikContext } from 'formik'

// Components
import BCartPicker from 'ui/blocks/Picker/types/cart'
import BPicker from 'ui/blocks/Picker'
import Icon from 'ui/components/Icon'
import { TimeslotsPicker } from './Picker'
import * as Drawer from 'ui/components/Picker/Drawer'

// Hooks
import { usePicker } from 'ui/components/Picker/hooks'

// Shared
import { useTranslation } from 'shared/utils/useTranslation'
import { Format as FormatUtils } from 'back_office/v2/utils/utils'

export const TimeslotsMobile = (props) => {
  const timeoutRef = useRef(null)
  const portalRef = useRef(null)

  const [open, setOpen] = useState(false)

  const picker = usePicker()
  const { t } = useTranslation('user')
  const form = useFormikContext()

  const timeslot = (props.options || []).find((option) => option.value === props.timeslotId)

  const hasError =
    !!(form.errors.starts_at?.date || form.errors.stops_at?.date) ||
    !!(form.errors.starts_at?.time || form.errors.stops_at?.time)

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  const selectWithTimeout = (selection) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    timeoutRef.current = setTimeout(() => {
      picker.setSelection(selection)
    }, 750)
  }

  const handleOpen = (e, type) => {
    e.preventDefault()

    setOpen(true)

    selectWithTimeout(type)
  }

  const handleTimeslotChange = (...args) => {
    props.onTimeslotChange(...args)

    if (!picker.hasInitialValues) {
      setOpen(false)
    }
  }

  const handleDrawerOpenChange = (open) => {
    // Reset selection on drawer close
    if (!open) {
      picker.setSelection(null)
    }

    setOpen(open)
  }

  return (
    <Fragment>
      <BCartPicker.Timeslots disabled={props.disabled} className="timeslots">
        <BPicker.Field>
          <BPicker.Label>{t('user.store.start_and_stop_date')}</BPicker.Label>
          <BPicker.Button
            onClick={(e) => handleOpen(e, 'from')}
            empty={!form.values.starts_at}
            error={hasError}
            data-tid="Starts at picker"
          >
            {form.values.starts_at ?
              FormatUtils.date(form.values.starts_at, 'date') :
              t('user.store.select_date_placeholder')}
          </BPicker.Button>
        </BPicker.Field>
        <BPicker.Field>
          <BPicker.TimeslotLabelWrapper>
            <BPicker.Label>{t('user.store.timeslot_from')}</BPicker.Label>
            <BPicker.Label>{t('user.store.timeslot_until')}</BPicker.Label>
          </BPicker.TimeslotLabelWrapper>
          <BPicker.Button onClick={(e) => handleOpen(e, 'timeslot')} empty={!timeslot} modifiers="width-100p">
            {timeslot && (
              <BPicker.Timeslot>
                {timeslot.label.split(' - ')[0]}
                <Icon icon="arrow-right" />
                {timeslot.label.split(' - ')[1]}
              </BPicker.Timeslot>
            )}
            {!timeslot && (
              <BPicker.Timeslot>
                {t('user.store.timeslot_from')}
                <Icon icon="arrow-right" />
                {t('user.store.timeslot_until')}
              </BPicker.Timeslot>
            )}
          </BPicker.Button>
        </BPicker.Field>
      </BCartPicker.Timeslots>
      <Drawer.Root open={open} onOpenChange={handleDrawerOpenChange}>
        <Drawer.Header>
          <Drawer.Title>{t('user.store.select_a_period')}</Drawer.Title>
          <TimeslotsPicker
            {...props}
            numberOfMonths={1}
            portalRef={portalRef}
            onTimeslotChange={handleTimeslotChange}
            maxHeight="100%"
            width="100%"
            usePortal
            isMobile
          />
        </Drawer.Header>
        <Drawer.Body>
          <BCartPicker.Portal ref={portalRef} />
        </Drawer.Body>
      </Drawer.Root>
    </Fragment>
  )
}

TimeslotsMobile.propTypes = {
  timeslotId: PropTypes.string,
  from: PropTypes.instanceOf(Date),
  to: PropTypes.instanceOf(Date),
  options: PropTypes.array,
  disabled: PropTypes.bool,
  onMonthChange: PropTypes.func,
  onBlur: PropTypes.func,
  onTimeslotChange: PropTypes.func
}
